import { useState } from "react"
import { Box, Button, CircularProgress, Tooltip, Typography } from "@mui/material"
import TimerIcon from "@mui/icons-material/TimerOutlined"
import StopIcon from "@mui/icons-material/StopCircleOutlined"

import { useTimer } from "../context/TimerContext"
import { usePermissions } from "../permissions"
import { clockFormat } from "../time/duration"
import StartTimerDialog from "./StartTimerDialog"

/**
 * One control in the app bar: start a timer, or watch the one that is running.
 *
 * ── Why it changes rather than sitting beside the timer ─────────────────────
 *
 * There is only ever one running timer per person — the database enforces it —
 * so "start" and "running" are two states of one thing, not two controls. A
 * Track Time button that stayed put while a timer ran would be inviting
 * somebody to start a second one, and what actually happens then is that the
 * first is stopped underneath them.
 *
 * ── Why this duplicates the bar below it ────────────────────────────────────
 *
 * It does, and only while both are on screen. The app bar is fixed and the
 * timer bar scrolls away with the page, so on any screen taller than a laptop's
 * the bar is gone by the time somebody is reading. The clock that never leaves
 * is the one that stops a timer being left running overnight, which is the
 * whole reason the bar exists.
 *
 * The rich controls — pause, resume, the long-running warning, the link back to
 * the task — stay on the bar. This is a clock and a stop button.
 */
export default function TrackTimeButton() {
  const { running, elapsed, busy, stop } = useTimer()
  const { can } = usePermissions()
  const [open, setOpen] = useState(false)

  // Logging time is a permission; without it this is a button that 403s.
  if (!can("time_tracking", "log")) return null

  if (running) {
    const label = running.task_code
      ? `${running.task_code} · ${clockFormat(elapsed)}`
      : running.description || running.project_name || running.client_label || "Running"

    return (
      <Tooltip title={running.paused ? "Paused — open the bar below to resume" : `Stop the timer (${label})`}>
        <Button
          variant="contained"
          color={running.paused ? "inherit" : "primary"}
          size="small"
          onClick={() => stop().catch(() => {})}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <StopIcon sx={{ fontSize: 18 }} />}
          sx={{ minWidth: 132, fontVariantNumeric: "tabular-nums" }}
          /* Deliberately not "Stop the running timer", which is the timer
             bar's name. Two controls with one accessible name is a screen
             reader announcing the same button twice with no way to tell which
             is which — and it is how this collided with the bar's own
             locator, which was the cheaper symptom of the same problem. */
          aria-label={`Stop tracking \u2014 ${clockFormat(elapsed)} elapsed`}
        >
          {/* The clock, not the task name: what somebody checks the app bar for
              is how long this has been going, and a name that changes width on
              every task makes the button jump about. */}
          <Box component="span" sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.1 }}>
            <Typography component="span" variant="body2" fontWeight={700} sx={{ fontVariantNumeric: "tabular-nums" }}>
              {clockFormat(elapsed)}
            </Typography>
            <Typography component="span" variant="caption" sx={{ opacity: 0.85, fontSize: 10 }}>
              {running.paused ? "Paused" : "Tracking"}
            </Typography>
          </Box>
        </Button>
      </Tooltip>
    )
  }

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        size="small"
        startIcon={<TimerIcon sx={{ fontSize: 18 }} />}
        onClick={() => setOpen(true)}
        sx={{ minWidth: 132, fontWeight: 600 }}
      >
        Track Time
      </Button>
      <StartTimerDialog open={open} onClose={() => setOpen(false)} />
    </>
  )
}
